import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/Input';
import PasswordInputField from '@/components/ui/PasswordInputField';
import { Select, SelectOption } from '@/components/ui/Select';
import SimpleSelect from '@/components/ui/SimpleSelect';
import Modal from '@/components/ui/Modal';
import { CreateScimConfigurationDto, UpdateScimConfigurationDto, ScimConfigurationDto } from '@/services/scim.service';
import { Loader2, AlertCircle, Info } from 'lucide-react';
import cron from 'cron-validator';

// Provider type options for dropdowns
const PROVIDER_TYPE_OPTIONS: SelectOption[] = [
  { label: 'SCIM 2.0 (Standard)', value: 'SCIM' },
  { label: 'Skillmine Auth (CustomGroups)', value: 'SKILLMINE_AUTH' },
  { label: 'Spotlight', value: 'SPOTLIGHT' },
];

const CONFLICT_RESOLUTION_OPTIONS: SelectOption[] = [
  {
    label: 'Skip - Keep existing data unchanged',
    value: 'SKIP',
  },
  {
    label: 'Update - Replace with provider data',
    value: 'UPDATE',
  },
  {
    label: 'Update + Additive Roles - Replace data but accumulate roles',
    value: 'UPDATE_ADDITIVE',
  },
  {
    label: 'Update + Preserve Local Groups - Keep skillzen_* group-roles protected',
    value: 'UPDATE_PRESERVE_LOCAL_GROUPS',
  },
  {
    label: 'Merge - Fill only empty fields',
    value: 'MERGE',
  },
];

interface ScimConfigurationFormData {
  provider_name: string;
  provider_type: string;
  base_url: string;
  token_endpoint: string;
  client_id: string;
  client_secret: string;
  scopes: string[];
  sync_config: {
    users: boolean;
    groups: boolean;
    roles: boolean;
    batch_size: number;
    conflict_resolution: string;
    auto_assign_default_group?: boolean;
    auto_sync_enabled?: boolean;
    sync_frequency?: string;
    sync_type?: 'full' | 'groups' | 'users';
  };
  is_active: boolean;
}

interface ScimConfigurationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateScimConfigurationDto | UpdateScimConfigurationDto) => Promise<void>;
  initialData?: ScimConfigurationDto | null;
  isLoading?: boolean;
}

export default function ScimConfigurationForm({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
}: ScimConfigurationFormProps) {
  const isEditMode = !!initialData;
  const [cronError, setCronError] = useState<string>('');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ScimConfigurationFormData>({
    // Validate while typing so errors (e.g. clean-text) show under the field, not only after Save
    mode: 'onChange',
    defaultValues: {
      provider_name: '',
      provider_type: 'SKILLMINE_AUTH',
      base_url: '',
      token_endpoint: '',
      client_id: '',
      client_secret: '',
      scopes: ['scim:read', 'scim:write'],
      sync_config: {
        users: true,
        groups: true,
        roles: true,
        batch_size: 100,
        conflict_resolution: 'SKIP',
        auto_assign_default_group: false,
        auto_sync_enabled: false,
        sync_frequency: '0 2 * * *',
      },
      is_active: true,
    },
  });

  const providerType = watch('provider_type');
  const syncConfigUsers = watch('sync_config.users');
  const syncConfigGroups = watch('sync_config.groups');
  const syncConfigRoles = watch('sync_config.roles');
  const batchSize = watch('sync_config.batch_size');
  const autoAssignDefaultGroup = watch('sync_config.auto_assign_default_group');
  const isActive = watch('is_active');

  // Reset form when modal opens with initial data
  useEffect(() => {
    if (isOpen && initialData) {
      reset({
        provider_name: initialData.provider_name || '',
        provider_type: initialData.provider_type || 'SKILLMINE_AUTH',
        base_url: initialData.base_url || '',
        token_endpoint: initialData.token_endpoint || '',
        client_id: initialData.client_id || '',
        client_secret: initialData.client_secret || '',
        scopes: initialData.scopes || ['scim:read', 'scim:write'],
        sync_config: {
          users: initialData.sync_config?.users ?? true,
          groups: initialData.sync_config?.groups ?? true,
          roles: initialData.sync_config?.roles ?? true,
          batch_size: initialData.sync_config?.batch_size ?? 100,
          conflict_resolution: initialData.sync_config?.conflict_resolution || 'SKIP',
          auto_assign_default_group: initialData.sync_config?.auto_assign_default_group ?? false,
          auto_sync_enabled: initialData.sync_config?.auto_sync_enabled ?? false,
          sync_frequency: initialData.sync_config?.sync_frequency || '0 2 * * *',
        },
        is_active: initialData.is_active ?? true,
      });
    } else if (isOpen && !initialData) {
      // Reset to defaults for create mode
      reset({
        provider_name: '',
        provider_type: 'SKILLMINE_AUTH',
        base_url: '',
        token_endpoint: '',
        client_id: '',
        client_secret: '',
        scopes: ['scim:read', 'scim:write'],
        sync_config: {
          users: true,
          groups: true,
          roles: true,
          batch_size: 100,
          conflict_resolution: 'SKIP',
          auto_assign_default_group: false,
          auto_sync_enabled: false,
          sync_frequency: '0 2 * * *',
        },
        is_active: true,
      });
    }
  }, [isOpen, initialData, reset]);

  // Validate cron expression
  const validateCron = (expression: string): boolean => {
    if (!expression || expression.trim() === '') {
      setCronError('Cron expression is required');
      return false;
    }

    const isValid = cron.isValidCron(expression, { alias: true, seconds: false });
    if (!isValid) {
      setCronError('Invalid cron expression. Format: minute hour day month weekday (e.g., "0 2 * * *")');
      return false;
    }

    setCronError('');
    return true;
  };

  const handleFormSubmit = async (data: ScimConfigurationFormData) => {
    // Validate custom cron expression if auto-sync is enabled
    if (data.sync_config?.auto_sync_enabled) {
      const freq = data.sync_config.sync_frequency || '0 2 * * *';
      if (!validateCron(freq)) {
        return; // Don't submit if cron is invalid
      }
    }

    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
      // Error handling is done in parent component
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !isLoading) {
      reset();
      onClose();
    }
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={handleClose}
        disabled={isSubmitting || isLoading}
        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Cancel
      </button>
      <button
        type="submit"
        onClick={handleSubmit(handleFormSubmit)}
        disabled={isSubmitting || isLoading}
        className="px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
      >
        {(isSubmitting || isLoading) && <Loader2 className="w-4 h-4 animate-spin" />}
        {isSubmitting || isLoading ? 'Saving...' : 'Save Configuration'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit SCIM Configuration' : 'Add SCIM Configuration'}
      subtitle={isEditMode ? 'Update your SCIM provider settings' : 'Configure a new SCIM identity provider'}
      size="xl"
      footer={footer}
      closeOnOverlayClick={!isSubmitting && !isLoading}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Basic Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              name="provider_name"
              control={control}
              label="Provider Name"
              placeholder="e.g., Azure AD Production"
              required
              error={errors.provider_name?.message}
              rules={{
                required: 'Provider name is required',
                minLength: {
                  value: 3,
                  message: 'Provider name must be at least 3 characters',
                },
              }}
            />

            <Select
              name="provider_type"
              control={control}
              label="Provider Type"
              options={PROVIDER_TYPE_OPTIONS}
              placeholder="Select provider type"
              required
              error={errors.provider_type?.message}
              rules={{ required: 'Provider type is required' }}
            />
          </div>
        </div>

        {/* Connection Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Connection Settings</h3>

          <Input
            name="base_url"
            control={control}
            label="Base URL"
            placeholder="https://api.example.com/scim/v2"
            required
            error={errors.base_url?.message}
            helperText="The base URL for your SCIM provider's API (HTTPS required for security)"
            rules={{
              required: 'Base URL is required',
              pattern: {
                value: /^https:\/\/.+/,
                message: 'Must be a valid HTTPS URL (HTTP is not allowed for security)',
              },
            }}
          />

          <Input
            name="token_endpoint"
            control={control}
            label="Token Endpoint"
            placeholder="https://login.example.com/oauth2/token"
            required
            error={errors.token_endpoint?.message}
            helperText="OAuth 2.0 token endpoint for authentication (HTTPS required)"
            rules={{
              required: 'Token endpoint is required',
              pattern: {
                value: /^https:\/\/.+/,
                message: 'Must be a valid HTTPS URL (HTTP is not allowed for security)',
              },
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              name="client_id"
              control={control}
              label="Client ID"
              placeholder="Enter client ID"
              required
              error={errors.client_id?.message}
              rules={{ required: 'Client ID is required' }}
            />

            <PasswordInputField
              name="client_secret"
              control={control}
              label="Client Secret"
              placeholder="Enter client secret"
              required
              error={errors.client_secret?.message}
              description={isEditMode ? 'Current value is shown. Update if needed.' : undefined}
              rules={
                isEditMode
                  ? undefined
                  : { required: 'Client secret is required' }
              }
            />
          </div>
        </div>

        {/* Sync Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Sync Configuration</h3>

          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            {/* Resource Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Resource Types to Sync
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-brand-indigo-300 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={syncConfigUsers}
                    onChange={(e) => {
                      const currentValue = watch('sync_config');
                      reset({
                        ...watch(),
                        sync_config: {
                          ...currentValue,
                          users: e.target.checked,
                        },
                      }, { keepDirty: true });
                    }}
                    className="w-4 h-4 text-brand-indigo-600 border-gray-300 rounded focus:ring-brand-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Sync Users</span>
                </label>

                <label className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-brand-indigo-300 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={syncConfigGroups}
                    onChange={(e) => {
                      const currentValue = watch('sync_config');
                      reset({
                        ...watch(),
                        sync_config: {
                          ...currentValue,
                          groups: e.target.checked,
                        },
                      }, { keepDirty: true });
                    }}
                    className="w-4 h-4 text-brand-indigo-600 border-gray-300 rounded focus:ring-brand-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Sync Groups</span>
                </label>

                <label className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-brand-indigo-300 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={syncConfigRoles}
                    onChange={(e) => {
                      const currentValue = watch('sync_config');
                      reset({
                        ...watch(),
                        sync_config: {
                          ...currentValue,
                          roles: e.target.checked,
                        },
                      }, { keepDirty: true });
                    }}
                    className="w-4 h-4 text-brand-indigo-600 border-gray-300 rounded focus:ring-brand-indigo-500"
                    disabled={providerType === 'SCIM'}
                  />
                  <span className={`text-sm font-medium ${providerType === 'SCIM' ? 'text-gray-400' : 'text-gray-700'}`}>
                    Sync Roles
                    {providerType === 'SCIM' && (
                      <span className="block text-xs text-gray-400">Not available for SCIM 2.0</span>
                    )}
                  </span>
                </label>
              </div>
              {providerType === 'SCIM' && (
                <p className="text-xs text-gray-500 mt-2">
                  Note: Standard SCIM 2.0 providers do not support role synchronization
                </p>
              )}
            </div>

            {/* Auto-Assign Default Group */}
            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-brand-indigo-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={autoAssignDefaultGroup}
                  onChange={(e) => {
                    const currentValue = watch('sync_config');
                    reset({
                      ...watch(),
                      sync_config: {
                        ...currentValue,
                        auto_assign_default_group: e.target.checked,
                      },
                    }, { keepDirty: true });
                  }}
                  className="w-4 h-4 text-brand-indigo-600 border-gray-300 rounded focus:ring-brand-indigo-500 mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900">Auto-assign Skillzen Learner Group</span>
                  <p className="text-xs text-gray-500 mt-1">
                    Automatically assign all synced users to the default "skillzen_learners" group with learner role. This ensures all users have minimum platform access.
                  </p>
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                    <strong>Group:</strong> skillzen_learners (SKILLZEN_LEARNERS)<br />
                    <strong>Role:</strong> skillzen_learners (Skillzen learners)
                  </div>
                </div>
              </label>
            </div>

            {/* Batch Size and Conflict Resolution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Batch Size
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={batchSize ?? ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;

                    // Allow empty input for editing
                    if (inputValue === '') {
                      setValue('sync_config.batch_size', '' as unknown as number, { shouldDirty: true });
                      return;
                    }

                    // Only allow positive integers (no negative, no decimals, no strings)
                    if (!/^\d+$/.test(inputValue)) {
                      return;
                    }

                    const numValue = parseInt(inputValue, 10);

                    // Enforce max limit
                    if (numValue > 1000) {
                      setValue('sync_config.batch_size', 1000, { shouldDirty: true });
                      return;
                    }

                    setValue('sync_config.batch_size', numValue, { shouldDirty: true });
                  }}
                  onBlur={() => {
                    // On blur, set default if empty or invalid
                    if (!batchSize || batchSize < 1) {
                      setValue('sync_config.batch_size', 100, { shouldDirty: true });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent ${
                    batchSize !== '' && (batchSize < 1 || batchSize > 1000)
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {batchSize !== '' && batchSize < 1 && (
                  <p className="mt-1 text-xs text-red-600">Batch size must be at least 1</p>
                )}
                {batchSize > 1000 && (
                  <p className="mt-1 text-xs text-red-600">Batch size cannot exceed 1000</p>
                )}

                <div className="mt-1 flex items-start gap-1 text-xs text-gray-600">
                  <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>
                    Smaller batches (50-100): Less memory, more API calls. Larger batches (200-500): Faster overall, more memory. Recommended: <strong>100-200</strong>
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conflict Resolution
                </label>
                <Select
                  name="sync_config.conflict_resolution"
                  control={control}
                  options={CONFLICT_RESOLUTION_OPTIONS}
                  placeholder="Select conflict resolution strategy"
                />
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900 space-y-1">
                  <div><strong>Skip:</strong> Ignores existing users/groups completely</div>
                  <div><strong>Update:</strong> Replaces all fields with provider data</div>
                  <div><strong>Update + Additive Roles:</strong> Replaces user/group data but accumulates permissions (roles keep adding)</div>
                  <div><strong>Update + Preserve Local Groups:</strong> Updates from SCIM but protects skillzen_* group-roles from removal</div>
                  <div><strong>Merge:</strong> Only fills empty fields, keeps existing data intact</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Automatic Sync Schedule */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Automatic Sync Schedule</h3>

          <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-indigo-300 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={watch('sync_config')?.auto_sync_enabled || false}
              onChange={(e) => {
                const currentValue = watch('sync_config');
                reset({
                  ...watch(),
                  sync_config: {
                    ...currentValue,
                    auto_sync_enabled: e.target.checked,
                    sync_frequency: currentValue?.sync_frequency || '0 2 * * *',
                    sync_type: currentValue?.sync_type || 'full',
                  },
                }, { keepDirty: true });
              }}
              className="w-5 h-5 text-brand-indigo-600 border-gray-300 rounded focus:ring-brand-indigo-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Enable Automatic Periodic Sync</span>
              <p className="text-xs text-gray-500">Automatically sync data based on a schedule</p>
            </div>
          </label>

          {watch('sync_config')?.auto_sync_enabled && (
            <div className="space-y-4 ml-4 pl-4 border-l-2 border-brand-indigo-200">
              {/* Sync Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sync Frequency
                </label>
                <SimpleSelect
                  value={(() => {
                    const freq = watch('sync_config')?.sync_frequency || '0 2 * * *';
                    const predefined = ['*/30 * * * *', '0 * * * *', '0 */6 * * *', '0 */12 * * *', '0 2 * * *', '0 0 * * 0', '0 0 1 * *'];
                    return predefined.includes(freq) ? freq : 'custom';
                  })()}
                  onChange={(value) => {
                    const selected = String(value || '0 2 * * *');
                    const currentValue = watch('sync_config');
                    if (selected !== 'custom') {
                      reset({
                        ...watch(),
                        sync_config: {
                          ...currentValue,
                          sync_frequency: selected,
                        },
                      }, { keepDirty: true });
                    } else {
                      // When "custom" is selected, set a placeholder value to trigger custom input
                      reset({
                        ...watch(),
                        sync_config: {
                          ...currentValue,
                          sync_frequency: currentValue?.sync_frequency &&
                            !['*/30 * * * *', '0 * * * *', '0 */6 * * *', '0 */12 * * *', '0 2 * * *', '0 0 * * 0', '0 0 1 * *'].includes(currentValue.sync_frequency)
                            ? currentValue.sync_frequency  // Keep existing custom value
                            : '0 3 * * *',  // Default custom value (3 AM daily)
                        },
                      }, { keepDirty: true });
                    }
                  }}
                  options={[
                    { value: '*/30 * * * *', label: 'Every 30 minutes' },
                    { value: '0 * * * *', label: 'Every 1 hour' },
                    { value: '0 */6 * * *', label: 'Every 6 hours' },
                    { value: '0 */12 * * *', label: 'Every 12 hours' },
                    { value: '0 2 * * *', label: 'Daily at 2 AM UTC' },
                    { value: '0 0 * * 0', label: 'Weekly on Sunday' },
                    { value: '0 0 1 * *', label: 'Monthly on 1st' },
                    { value: 'custom', label: 'Custom (Cron Expression)' },
                  ]}
                  isSearchable={false}
                />
                <p className="text-xs text-gray-500 mt-1">
                  When to automatically run synchronization
                </p>

                {/* Custom Cron Input */}
                {(() => {
                  const freq = watch('sync_config')?.sync_frequency || '0 2 * * *';
                  // Fixed: Include all 7 predefined cron expressions (matches line 490)
                  const predefined = ['*/30 * * * *', '0 * * * *', '0 */6 * * *', '0 */12 * * *', '0 2 * * *', '0 0 * * 0', '0 0 1 * *'];
                  return !predefined.includes(freq);
                })() && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Cron Expression
                    </label>
                    <input
                      type="text"
                      value={watch('sync_config')?.sync_frequency || ''}
                      onChange={(e) => {
                        const currentValue = watch('sync_config');
                        const newValue = e.target.value;
                        reset({
                          ...watch(),
                          sync_config: {
                            ...currentValue,
                            sync_frequency: newValue,
                          },
                        }, { keepDirty: true });
                        // Validate on change
                        if (newValue) validateCron(newValue);
                      }}
                      onBlur={(e) => validateCron(e.target.value)}
                      placeholder="0 2 * * *"
                      className={`w-full px-3 py-2 font-mono text-sm border rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent ${
                        cronError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {cronError ? (
                      <div className="mt-2 flex items-start gap-2 text-xs text-red-600">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{cronError}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Format: <span className="font-mono">minute hour day month weekday</span> (e.g., <span className="font-mono">0 3 * * *</span> = 3 AM daily)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Next Scheduled Sync Display */}
              {watch('sync_config')?.next_scheduled_sync && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-900">
                    Next Sync: {new Date(watch('sync_config')!.next_scheduled_sync!).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Activation Status */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Status</h3>

          <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-indigo-300 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => {
                reset({
                  ...watch(),
                  is_active: e.target.checked,
                }, { keepDirty: true });
              }}
              className="w-5 h-5 text-brand-indigo-600 border-gray-300 rounded focus:ring-brand-indigo-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Active Configuration</span>
              <p className="text-xs text-gray-500">Enable this configuration for synchronization</p>
            </div>
          </label>
        </div>
      </form>
    </Modal>
  );
}
